<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  catalog: { type: Object, default: () => ({}) },
  value: { type: Object, required: true },
});

const emit = defineEmits(['update:value']);
const { t } = useI18n();
const paymentMethod = computed(
  () => props.catalog.property?.payment_method?.toString() || ''
);
const needsSeller = computed(() => paymentMethod.value === '1');
const needsBank = computed(() => ['2', '7'].includes(paymentMethod.value));
const attentionPoint = computed(() => {
  if (needsSeller.value) {
    return {
      tag: t('POST_SALE.TOPICS.DELIVERY.ATTENTION.CASH_TAG'),
      text: t('POST_SALE.TOPICS.DELIVERY.ATTENTION.CASH_TEXT'),
    };
  }
  if (needsBank.value) {
    return {
      tag: t('POST_SALE.TOPICS.DELIVERY.ATTENTION.FINANCING_TAG'),
      text: t('POST_SALE.TOPICS.DELIVERY.ATTENTION.FINANCING_TEXT'),
    };
  }

  return null;
});
const update = (field, value) =>
  emit('update:value', {
    ...props.value,
    payment_method: paymentMethod.value,
    [field]: value,
  });
</script>

<template>
  <div class="grid gap-4">
    <section
      class="grid gap-3 rounded-lais border border-lais-border-soft bg-white p-4"
    >
      <h2 class="text-lg font-semibold text-lais-text">
        {{ t('POST_SALE.TOPICS.DELIVERY.PAYMENT_TITLE') }}
      </h2>
      <label class="grid gap-1 text-sm font-semibold text-lais-text-body">
        {{ t('POST_SALE.TOPICS.DELIVERY.PAYMENT_METHOD') }}
        <select
          data-testid="post-sale-delivery-payment-method"
          :value="paymentMethod"
          class="min-h-10 rounded-lais border border-lais-border bg-lais-bg-soft px-2 font-normal"
          disabled
        >
          <option value="">{{ t('POST_SALE.TOPICS.COMMON.SELECT') }}</option>
          <option
            v-for="option in catalog.payment_methods || []"
            :key="option.id"
            :value="option.id"
          >
            {{ option.label }}
          </option>
        </select>
      </label>
    </section>
    <section
      v-if="needsSeller"
      class="grid gap-3 rounded-lais border border-lais-border-soft bg-white p-4"
    >
      <h2 class="text-lg font-semibold text-lais-text">
        {{ t('POST_SALE.TOPICS.DELIVERY.SELLER_TITLE') }}
      </h2>
      <label class="grid gap-1 text-sm font-semibold text-lais-text-body">
        {{ t('POST_SALE.TOPICS.DELIVERY.SELLER') }}
        <select
          data-testid="post-sale-delivery-seller"
          :value="value.responsible_seller_id"
          class="min-h-10 rounded-lais border border-lais-border bg-white px-2 font-normal"
          @change="update('responsible_seller_id', $event.target.value)"
        >
          <option value="">{{ t('POST_SALE.TOPICS.COMMON.SELECT') }}</option>
          <option
            v-for="option in catalog.sellers || []"
            :key="option.id"
            :value="option.id"
          >
            {{ option.name }}
          </option>
        </select>
      </label>
    </section>
    <section
      v-if="needsBank"
      class="grid gap-3 rounded-lais border border-lais-border-soft bg-white p-4"
    >
      <h2 class="text-lg font-semibold text-lais-text">
        {{ t('POST_SALE.TOPICS.DELIVERY.BANK_TITLE') }}
      </h2>
      <label class="grid gap-1 text-sm font-semibold text-lais-text-body">
        {{ t('POST_SALE.TOPICS.DELIVERY.BANK') }}
        <select
          data-testid="post-sale-delivery-bank"
          :value="value.bank_id"
          class="min-h-10 rounded-lais border border-lais-border bg-white px-2 font-normal"
          @change="update('bank_id', $event.target.value)"
        >
          <option value="">{{ t('POST_SALE.TOPICS.COMMON.SELECT') }}</option>
          <option
            v-for="option in catalog.banks || []"
            :key="option.id"
            :value="option.id"
          >
            {{ option.name }}
          </option>
        </select>
      </label>
    </section>
    <section
      v-if="attentionPoint"
      data-testid="post-sale-delivery-attention"
      class="rounded-lais border border-lais-border-soft bg-white p-4 shadow-lais-soft"
    >
      <p class="text-sm font-semibold text-lais-text-body">
        {{ t('POST_SALE.TOPICS.DELIVERY.ATTENTION.TITLE') }}
      </p>
      <span
        class="mt-3 inline-flex rounded-full bg-lais-primary-50 px-2 py-1 text-xs font-semibold text-lais-primary"
      >
        {{ attentionPoint.tag }}
      </span>
      <p class="mt-3 text-sm font-semibold text-lais-text-body">
        {{ attentionPoint.text }}
      </p>
    </section>
  </div>
</template>
